// noinspection DuplicatedCode
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import Button from "@material-tailwind/react/Button";
import Modal from "@material-tailwind/react/Modal";
import ModalBody from "@material-tailwind/react/ModalBody";
import ModalFooter from "@material-tailwind/react/ModalFooter";
import ModalHeader from "@material-tailwind/react/ModalHeader";

import {
  getKioskDeviceList,
  generateKioskDeviceCode,
  revokeKioskDevice,
  unpairKioskDevice,
  clearKioskDeviceMessage,
  clearLastGeneratedCode,
  kioskDeviceSelector,
} from "./kioskDeviceSlice";

import { Colors } from "../../../../assets/Colors";
import Loading from "../../../../components/loading/Loading";

const KioskDevicePage = () => {
  const dispatch = useDispatch();
  const {
    data,
    pagination,
    isGetFetching,
    isGenerateFetching,
    isActionFetching,
    lastGeneratedCode,
    message,
    errorMessage,
  } = useSelector(kioskDeviceSelector);

  const [searchInput, setSearchInput] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [namaPerangkat, setNamaPerangkat] = useState("");

  const fetchData = useCallback(
    (page = 1) => {
      dispatch(getKioskDeviceList({ search: searchInput || undefined, page, page_size: 10 }));
    },
    [dispatch, searchInput]
  );

  useEffect(() => {
    fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialLoad && !isGetFetching) setIsInitialLoad(false);
  }, [isGetFetching, isInitialLoad]);

  useEffect(() => {
    if (message) {
      toast.success(message, { position: "bottom-right" });
      dispatch(clearKioskDeviceMessage());
    }
    if (errorMessage) {
      toast.error(errorMessage, { position: "bottom-right" });
      dispatch(clearKioskDeviceMessage());
    }
  }, [message, errorMessage, dispatch]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(1);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!namaPerangkat.trim()) {
      toast.error("Nama perangkat wajib diisi", { position: "bottom-right" });
      return;
    }
    try {
      await dispatch(generateKioskDeviceCode({ Nama: namaPerangkat })).unwrap();
      setNamaPerangkat("");
      fetchData(pagination.currentPage);
    } catch (e) {
      // toast sudah muncul lewat errorMessage effect
    }
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    dispatch(clearLastGeneratedCode());
  };

  const handleRevoke = async (row) => {
    if (!window.confirm(`Nonaktifkan device "${row.Nama}"? Tablet yang sudah dipasangkan langsung tak bisa submit absen lagi.`)) {
      return;
    }
    await dispatch(revokeKioskDevice(row.DeviceID));
    fetchData(pagination.currentPage);
  };

  const handleUnpair = async (row) => {
    if (!window.confirm(`Lepas pairing device "${row.Nama}"? Kode ${row.DeviceCode} bisa dipakai lagi utk memasangkan tablet baru.`)) {
      return;
    }
    await dispatch(unpairKioskDevice(row.DeviceID));
    fetchData(pagination.currentPage);
  };

  if (isInitialLoad && isGetFetching) {
    return <Loading />;
  }

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>

        <div className="px-3 md:px-8 h-auto -mt-24">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
                <div className="rounded-t mb-0 px-4 py-3 border-0">
                  <div className="flex flex-wrap items-center">
                    <div className="relative w-full px-4 max-w-full flex-grow flex-1">
                      <h3 className="font-semibold text-lg text-blueGray-700">
                        Device Absensi Wajah
                      </h3>
                      <p className="text-sm text-blueGray-400 mb-2">
                        Kode perangkat dipakai SEKALI untuk memasangkan tablet di
                        halaman kiosk (/kiosk/absensi-wajah). Setelah terpasang,
                        tablet tetap login sampai device dicabut/dilepas dari sini.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <form onSubmit={handleSearch} className="flex gap-2">
                          <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Cari nama / kode device..."
                            className="border rounded px-3 py-2 text-sm w-64"
                          />
                          <Button color="lightBlue" buttonType="filled" size="sm" ripple="light" type="submit">
                            Cari
                          </Button>
                        </form>
                        <Button
                          color="green"
                          buttonType="filled"
                          size="sm"
                          ripple="light"
                          onClick={() => setShowGenerateModal(true)}
                        >
                          + Generate Device Baru
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="block w-full overflow-x-auto">
                  <table className="items-center w-full bg-transparent border-collapse">
                    <thead>
                      <tr>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Nama</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Kode</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Status</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Pairing</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-left bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Terakhir Aktif</th>
                        <th className="px-4 py-3 text-xs uppercase font-semibold text-center bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center py-6 text-sm text-blueGray-400">
                            Belum ada device
                          </td>
                        </tr>
                      )}
                      {data.map((row) => (
                        <tr key={row.DeviceID}>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4">{row.Nama}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 font-mono">{row.DeviceCode}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">
                            {row.Status === 1 ? (
                              <span className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs">Aktif</span>
                            ) : (
                              <span className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs">Nonaktif</span>
                            )}
                          </td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">
                            {row.Paired ? (
                              <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs">Terpasang</span>
                            ) : (
                              <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs">Belum</span>
                            )}
                          </td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4">{row.LastSeenAt || "-"}</td>
                          <td className="border-t-0 px-4 py-3 text-xs whitespace-nowrap p-4 text-center">
                            {row.Paired && row.Status === 1 && (
                              <Button
                                color="orange"
                                buttonType="outline"
                                size="sm"
                                ripple="light"
                                className="mr-2"
                                disabled={isActionFetching}
                                onClick={() => handleUnpair(row)}
                              >
                                Lepas Pairing
                              </Button>
                            )}
                            {row.Status === 1 && (
                              <Button
                                color="red"
                                buttonType="outline"
                                size="sm"
                                ripple="light"
                                disabled={isActionFetching}
                                onClick={() => handleRevoke(row)}
                              >
                                Nonaktifkan
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center px-4 py-3">
                  <span className="text-xs text-blueGray-400">
                    Halaman {pagination.currentPage} dari {pagination.totalPages || 1} ({pagination.totalCount} device)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      color="blueGray"
                      buttonType="outline"
                      size="sm"
                      disabled={!pagination.hasPrev || isGetFetching}
                      onClick={() => fetchData(pagination.currentPage - 1)}
                    >
                      Sebelumnya
                    </Button>
                    <Button
                      color="blueGray"
                      buttonType="outline"
                      size="sm"
                      disabled={!pagination.hasNext || isGetFetching}
                      onClick={() => fetchData(pagination.currentPage + 1)}
                    >
                      Berikutnya
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal size="sm" active={showGenerateModal} toggler={closeGenerateModal}>
        <ModalHeader toggler={closeGenerateModal}>Generate Device Baru</ModalHeader>
        <ModalBody>
          {!lastGeneratedCode ? (
            <form onSubmit={handleGenerate}>
              <label className="block text-xs uppercase text-blueGray-500 mb-1">
                Nama Perangkat / Lokasi
              </label>
              <input
                type="text"
                value={namaPerangkat}
                onChange={(e) => setNamaPerangkat(e.target.value)}
                placeholder="mis. Tablet Gerbang Depan"
                className="border rounded px-3 py-2 text-sm w-full"
                autoFocus
              />
            </form>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-blueGray-500 mb-2">
                Kode untuk "{lastGeneratedCode.Nama}" — ketikkan di tablet pada
                halaman <span className="font-mono">/kiosk/absensi-wajah</span>:
              </p>
              <p className="text-4xl font-bold tracking-widest font-mono text-lightBlue-600">
                {lastGeneratedCode.DeviceCode}
              </p>
              <p className="text-xs text-blueGray-400 mt-2">
                Kode ini hanya berlaku SEKALI untuk pairing. Kalau tablet perlu
                dipasangkan ulang, lepas pairing device ini dulu dari daftar.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="blueGray" buttonType="link" onClick={closeGenerateModal} ripple="dark">
            {lastGeneratedCode ? "Tutup" : "Batal"}
          </Button>
          {!lastGeneratedCode && (
            <Button
              color="lightBlue"
              buttonType="filled"
              onClick={handleGenerate}
              ripple="light"
              disabled={isGenerateFetching}
            >
              {isGenerateFetching ? "Membuat..." : "Generate"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default KioskDevicePage;
